/**
 * Field-list spacing for header, customer, and signature blocks.
 *
 * The editor preview reserves an inset the exported document does not draw.
 * Export-mode HTML and React-PDF share the PDF metrics from before commit 373a9a4.
 * Pixel lengths convert with `px * 0.75`. Point lengths were raw PDF points and must
 * not go through that conversion a second time.
 */

/** Editor-preview chrome only. Export mode and React-PDF do not render these frames. */
export const FIELD_LIST_EDITOR_INSET_PX = {
  sectionPaddingY: 8,
  elementPaddingY: 4
} as const;

export const FIELD_LIST_EDITOR_LINE_HEIGHT = 1.35;

/** Body metrics for the editor preview (not the exported document). */
export const FIELD_LIST_EDITOR_PX = {
  columnGap: 16,
  stackedRowGap: 4,
  halfColumnPaddingEnd: 16,
  labelMarginBottom: 2,
  labelFontSize: 11,
  valueMinHeight: 18,
  captionFontSize: 11,
  captionRuleMarginBottom: 4,
  signatureLineHeight: 32
} as const;

/**
 * Exported-document lengths that the pre-373a9a4 PDF passed through `pxToPt`.
 * Section inset applies to real header sections only. The flat customer/signature
 * layout did not have it.
 */
export const FIELD_LIST_EXPORT_PX = {
  sectionedFieldPaddingBottom: 6,
  sectionInsetY: 8,
  columnGap: 16,
  signatureLineHeight: 32
} as const;

/** Exported-document lengths that the pre-373a9a4 PDF stored as raw points. */
export const FIELD_LIST_EXPORT_PT = {
  flatFieldPaddingBottom: 6,
  halfColumnPaddingEnd: 8,
  labelFontSize: 8,
  labelMarginBottom: 2,
  captionFontSize: 8,
  captionRuleMarginBottom: 4,
  imageCaptionMarginTop: 4
} as const;

/**
 * Horizontal space between the label and the value of a side-by-side `label_value` field. One length for
 * the HTML preview (the flex `gap`) and the exported PDF (the label's right margin, through `pxToPt`:
 * 8 px = 6 pt).
 */
export const FIELD_LIST_LABEL_VALUE_GAP_PX = 8;

export interface FieldListTextMetrics {
  labelFontSize: number;
  labelMarginBottom: number;
  labelLineHeight?: number;
  valueMinHeight?: number;
  valueLineHeight?: number;
  captionFontSize: number;
  captionRuleMarginBottom: number;
  signatureLineHeight: number;
  signatureCaptionLineHeight?: number;
  imageCaptionFontSize: number;
  imageCaptionMarginTop: number;
}

export const EDITOR_FIELD_TEXT_METRICS: FieldListTextMetrics = {
  labelFontSize: FIELD_LIST_EDITOR_PX.labelFontSize,
  labelMarginBottom: FIELD_LIST_EDITOR_PX.labelMarginBottom,
  labelLineHeight: FIELD_LIST_EDITOR_LINE_HEIGHT,
  valueMinHeight: FIELD_LIST_EDITOR_PX.valueMinHeight,
  valueLineHeight: FIELD_LIST_EDITOR_LINE_HEIGHT,
  captionFontSize: FIELD_LIST_EDITOR_PX.captionFontSize,
  captionRuleMarginBottom: FIELD_LIST_EDITOR_PX.captionRuleMarginBottom,
  signatureLineHeight: FIELD_LIST_EDITOR_PX.signatureLineHeight,
  signatureCaptionLineHeight: FIELD_LIST_EDITOR_LINE_HEIGHT,
  imageCaptionFontSize: FIELD_LIST_EDITOR_PX.captionFontSize,
  imageCaptionMarginTop: FIELD_LIST_EDITOR_PX.captionRuleMarginBottom
};

/** Inverse of `px * 0.75`, so a raw PDF point matches the export HTML pixel length. */
export function cssPxFromPdfPt(pt: number): number {
  return pt / 0.75;
}

export const EXPORT_FIELD_TEXT_METRICS_CSS: FieldListTextMetrics = {
  labelFontSize: cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.labelFontSize),
  labelMarginBottom: cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.labelMarginBottom),
  captionFontSize: cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.captionFontSize),
  captionRuleMarginBottom: cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.captionRuleMarginBottom),
  signatureLineHeight: FIELD_LIST_EXPORT_PX.signatureLineHeight,
  imageCaptionFontSize: cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.captionFontSize),
  imageCaptionMarginTop: cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.imageCaptionMarginTop)
};
